import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Ban, Undo2, ExternalLink, Ruler } from "lucide-react";
import { drw, errText } from "./api";

/** MARK-LINK-1 §4 / MARK-LINK-2 — everything that did not resolve to an FRS room-product, grouped
 *  BY VALUE: one unresolved subject covering 30 marks is ONE row to answer, and the answer is kept
 *  for every future upload on this job. A room that is not on the FRS has three ways out —
 *  reassign it, rule the takeoff wrong (reversible, remembered across re-uploads), or add the room
 *  to the FRS and let the mark re-match itself. Nothing to answer → this panel says so. */
export default function LinkReview({ setId, onChanged }) {
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState("");
  const [pick, setPick] = useState({});

  const load = useCallback(async () => {
    try {
      const [rev, rep] = await Promise.all([
        drw.get(`/sets/${setId}/link-review`),
        drw.get(`/sets/${setId}/link-report`),
      ]);
      setData(rev.data);
      setReport(rep.data);
    } catch { setData(null); }
  }, [setId]);
  useEffect(() => { if (setId) load(); }, [setId, load]);

  const refreshReport = () => drw.get(`/sets/${setId}/link-report`)
    .then(({ data: rep }) => setReport(rep)).catch(() => {});

  const fix = async (row) => {
    const target = pick[`${row.kind}:${row.value}`];
    if (!target) return;
    setBusy(`${row.kind}:${row.value}`);
    try {
      const { data: res } = await drw.post(`/sets/${setId}/link-fix`, { kind: row.kind, value: row.value, target });
      toast.success(`${row.value} → ${target} · ${row.marks} mark${row.marks === 1 ? "" : "s"} re-read`);
      setData(res.review);
      onChanged?.();
      refreshReport();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };

  const exclude = async (row, undo = false) => {
    const on = row.kind || row.on;
    const key = `x:${on}:${row.value}`;
    setBusy(key);
    try {
      const { data: res } = await drw.post(`/sets/${setId}/link-exclude`, { on, value: row.value, undo });
      toast.success(undo ? `${row.value} is back in the takeoff` : `${row.value} excluded as a wrong takeoff`);
      setData(res.review);
      onChanged?.();
      refreshReport();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };

  if (!report) return null;
  const rows = data?.rows || [];
  const fixable = rows.filter((r) => r.kind !== "no_room");
  const noRoom = rows.filter((r) => r.kind === "no_room");
  const excluded = data?.excluded || [];
  const lengths = report.length_takeoffs || [];
  const pid = data?.project_id || report.project_id;
  return (
    <div className="text-xs rounded-lg border bg-white p-2 space-y-2" data-testid="link-review">
      <div className="font-semibold text-sm">Takeoff marks against the FRS</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1" data-testid="link-report-numbers">
        <span>Markups read</span><b data-testid="link-markups">{report.markups}</b>
        <span>Takeoff marks</span><b data-testid="link-takeoff">{report.takeoff_marks}</b>
        <span>Linked to one FRS room-product</span><b className="text-green-800" data-testid="link-linked">{report.linked} · {report.linked_units} unit{report.linked_units === 1 ? "" : "s"}</b>
        <span>Open the room's product list</span><b data-testid="link-room-level">{report.room_level}</b>
        <span>Not linked</span><b className={report.unresolved ? "text-amber-800" : "text-neutral-400"} data-testid="link-unresolved">{report.unresolved}</b>
        {report.excluded > 0 && (<>
          <span>Excluded as wrong</span><b className="text-neutral-500" data-testid="link-excluded-count">{report.excluded}</b>
        </>)}
      </div>
      <div className="text-[11px] text-neutral-500" data-testid="link-count-note">{report.count_note}</div>
      {report.quantity_mismatches?.length > 0 && (
        <div className="rounded border border-amber-200 bg-amber-50 p-2 space-y-1" data-testid="link-mismatches">
          <div className="font-semibold text-amber-900">{report.quantity_mismatches.length} room-product where the marks and the FRS quantity differ</div>
          {report.quantity_mismatches.slice(0, 8).map((m) => (
            <div key={`${m.room_number}-${m.scope_code}-${m.product_code}`} className="text-[11px] text-amber-900" data-testid={`link-mismatch-${m.room_number}-${m.product_code}`}>
              Room {m.room_number} · {m.scope_code} {m.product_code}: {m.marks} mark{m.marks === 1 ? "" : "s"} = {m.mark_units} unit{m.mark_units === 1 ? "" : "s"}, FRS asks {m.frs_quantity}
            </div>
          ))}
        </div>
      )}
      {fixable.length === 0 ? (
        <div className="text-green-800 font-medium" data-testid="link-review-clear">Nothing to answer — every mark that carries a room resolved.</div>
      ) : (
        <div className="space-y-1" data-testid="link-review-rows">
          {fixable.map((row) => {
            const key = `${row.kind}:${row.value}`;
            const xkey = `x:${row.kind}:${row.value}`;
            return (
              <div key={key} className="rounded border p-2 space-y-1" data-testid={`link-row-${row.kind}-${row.value}`}>
                <div className="font-medium">{row.question}</div>
                <div className="text-[11px] text-neutral-500">{row.marks} mark{row.marks === 1 ? "" : "s"} · {row.units} unit{row.units === 1 ? "" : "s"} · sheets {row.sheets.slice(0, 6).join(", ")}{row.sheets.length > 6 ? "…" : ""}</div>
                <div className="flex items-center gap-1">
                  <select value={pick[key] || ""} onChange={(e) => setPick({ ...pick, [key]: e.target.value })}
                          className="h-8 border rounded-md px-1 flex-1 min-w-0" data-testid={`link-pick-${row.kind}-${row.value}`}>
                    <option value="">— choose —</option>
                    {(row.choices || []).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button type="button" disabled={!pick[key] || busy === key} onClick={() => fix(row)}
                          className="h-8 px-3 rounded-md bg-neutral-900 text-white font-semibold disabled:opacity-40 inline-flex items-center gap-1"
                          data-testid={`link-apply-${row.kind}-${row.value}`}>
                    {busy === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Fix all {row.marks}
                  </button>
                </div>
                {(row.can_exclude || row.frs_room_missing) && (
                  <div className="flex flex-wrap items-center gap-2 pt-0.5">
                    {row.frs_room_missing && pid && (
                      <a href={`/projects/${pid}?tab=frs`} target="_blank" rel="noreferrer"
                         className="h-7 px-2 rounded-md border border-neutral-300 inline-flex items-center gap-1 font-medium hover:bg-neutral-50"
                         data-testid={`link-add-frs-${row.value}`}>
                        <ExternalLink className="w-3 h-3" /> Add room {row.add_to_frs} to the FRS
                      </a>
                    )}
                    {row.can_exclude && (
                      <button type="button" disabled={busy === xkey} onClick={() => exclude(row)}
                              className="h-7 px-2 rounded-md border border-rose-300 text-rose-800 inline-flex items-center gap-1 font-medium hover:bg-rose-50"
                              data-testid={`link-exclude-${row.kind}-${row.value}`}>
                        {busy === xkey ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3" />} Wrong takeoff — exclude
                      </button>
                    )}
                    {row.frs_room_missing && (
                      <span className="text-[11px] text-neutral-500">Add the room and the mark links itself — no re-upload.</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {lengths.length > 0 && (
        <div className="rounded border border-sky-200 bg-sky-50 p-2 space-y-0.5" data-testid="link-lengths">
          <div className="font-semibold text-sky-900 inline-flex items-center gap-1"><Ruler className="w-3.5 h-3.5" /> {lengths.length} length takeoff{lengths.length === 1 ? "" : "s"}</div>
          {lengths.map((l, i) => (
            <div key={`${l.scope_code}-${l.room_number}-${i}`} className="text-[11px] text-sky-900" data-testid={`link-length-${l.scope_code}-${l.room_number}`}>
              {l.scope_code} · room {l.room_number} · {l.label} · <b>{l.measured}</b> → {l.maps_to}
            </div>
          ))}
          <div className="text-[11px] text-sky-800">{report.length_note}</div>
        </div>
      )}
      {excluded.length > 0 && (
        <div className="rounded border border-neutral-200 bg-neutral-50 p-2 space-y-1" data-testid="link-excluded">
          <div className="font-semibold">Excluded as wrong takeoffs</div>
          {excluded.map((x) => (
            <div key={`${x.on}-${x.value}`} className="flex items-center justify-between gap-2" data-testid={`link-excluded-${x.on}-${x.value}`}>
              <span className="text-[11px] text-neutral-600">{x.on} “{x.value}” · {x.by || "unknown"} · {(x.at || "").slice(0, 16).replace("T", " ")}</span>
              <button type="button" disabled={busy === `x:${x.on}:${x.value}`} onClick={() => exclude(x, true)}
                      className="h-7 px-2 rounded-md border border-neutral-300 inline-flex items-center gap-1 font-medium hover:bg-white shrink-0"
                      data-testid={`link-restore-${x.on}-${x.value}`}>
                {busy === `x:${x.on}:${x.value}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />} Put it back
              </button>
            </div>
          ))}
        </div>
      )}
      {noRoom.length > 0 && (
        <div className="rounded border border-neutral-200 bg-neutral-50 p-2 space-y-0.5" data-testid="link-no-room">
          <div className="font-semibold">No Room Number in Bluebeam on these markups</div>
          {noRoom.map((row) => (
            <div key={row.value} className="text-[11px] text-neutral-600" data-testid={`link-no-room-${row.value}`}>{row.value} · {row.marks} mark{row.marks === 1 ? "" : "s"} · sheets {row.sheets.slice(0, 5).join(", ")}</div>
          ))}
          <div className="text-[11px] text-neutral-500">Add the room number in Bluebeam and upload the set again.</div>
        </div>
      )}
    </div>
  );
}
